/** Thin client for the coverage backend API. */

export interface Station {
  id: number;
  identifier: string;
  lat: number;
  lon: number;
  antenna_height_agl_m: number;
  agl_derivation: "derived" | "fallback";
  connected: boolean;
}

export interface StationDetail extends Station {
  station_altitude_ellipsoid_m: number;
  raw_row: string[];
}

export type ConnectedFilter = "all" | "connected_only" | "disconnected_only";
export type Frequency = "1090" | "978";
export type NationalRegion = "conus" | "uk";

// National-overlay region extents (WGS84 [[lon_min,lat_min],[lon_max,lat_max]]),
// matching the backend REGIONS registry. Used to fly the map to the region.
export const NATIONAL_REGION_BOUNDS: Record<NationalRegion, [[number, number], [number, number]]> = {
  conus: [[-125.0, 24.5], [-66.5, 49.5]],
  uk: [[-11.0, 49.0], [2.0, 61.0]],
};
export type JobMode = "union" | "per_station";

export interface BBox {
  lon_min: number;
  lat_min: number;
  lon_max: number;
  lat_max: number;
}

export interface ExtraObserver {
  lat: number;
  lon: number;
  agl_m: number;
  identifier: string;
}

export interface JobCreate {
  bbox: BBox;
  mode: JobMode;
  selected_station_id?: number | null;
  station_filter: ConnectedFilter;
  use_cache?: boolean;
  extra_observers?: ExtraObserver[];
}

export interface ObserverMeta {
  station_id: number;
  identifier: string;
  lat: number;
  lon: number;
  antenna_height_agl_m: number;
  merged_member_ids: number[];
  cache_hit: boolean;
  compute_sec: number;
}

export interface Job {
  id: string;
  bbox_wkt: string;
  mode: string;
  selected_station_id: number | null;
  station_filter: ConnectedFilter;
  status: "queued" | "running" | "done" | "failed";
  progress: number;
  stations_in_scope: number | null;
  stations_completed: number;
  cog_path_1090: string | null;
  cog_path_978: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
  extra_observers?: ExtraObserver[];
}

const BASE = "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json();
}

export interface IngestStatus {
  state: "idle" | "running" | "done" | "failed";
  processed: number;
  total: number | null;
  derived_agl_ok: number;
  derived_agl_fallback: number;
  started_at: number | null;
  completed_at: number | null;
  error: string | null;
  source: string;
  elapsed_sec: number;
  eta_sec: number | null;
}

export const api = {
  listStations: (filter: ConnectedFilter = "all") =>
    req<Station[]>(`/stations?connected=${filter}`),

  getStation: (id: number) => req<StationDetail>(`/stations/${id}`),

  ingestStatus: () => req<IngestStatus>("/stations/ingest-status"),

  uploadCsv: async (file: File): Promise<{ accepted: boolean; filename: string }> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${BASE}/stations/upload`, { method: "POST", body: form });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },

  reingestSeed: async (): Promise<{ accepted: boolean; source: string }> => {
    const res = await fetch(`${BASE}/stations/reingest-seed`, { method: "POST" });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },

  createJob: (body: JobCreate) =>
    req<Job>("/jobs", { method: "POST", body: JSON.stringify(body) }),

  listJobs: (status: "queued" | "running" | "done" | "failed" = "done", limit = 100) =>
    req<Job[]>(`/jobs?status=${status}&limit=${limit}`),

  getJob: (id: string) => req<Job>(`/jobs/${id}`),

  jobObservers: (id: string) =>
    req<{ observers: ObserverMeta[]; altitude_tiers_ft?: number[]; dem_resolution_m?: number }>(
      `/jobs/${id}/observers`,
    ),

  cancelJob: (id: string) =>
    req<{ status: string; was?: string; noop?: boolean }>(`/jobs/${id}/cancel`, {
      method: "POST",
    }),

  stationDiagnostics: (id: number) =>
    req<{
      id: number;
      identifier: string;
      lat: number;
      lon: number;
      connected: boolean;
      station_altitude_ellipsoid_m: number;
      dem_orthometric_m: number | null;
      geoid_n_m: number | null;
      dem_ellipsoidal_m: number | null;
      dem_source: string;
      derived_agl_m: number;
      agl_derivation: string;
      agl_override_m: number | null;
      agl_override_set_at: string | null;
      effective_agl_m: number;
    }>(`/stations/${id}/diagnostics`),

  setAglOverride: (id: number, agl_override_m: number | null) =>
    req<{ id: number; agl_override_m: number | null }>(`/stations/${id}/agl-override`, {
      method: "PUT",
      body: JSON.stringify({ agl_override_m }),
    }),

  stationDiagnosticsCsvUrl: () => `${BASE}/stations/diagnostics.csv`,

  cogUrl: (id: string, freq: Frequency) => `${BASE}/jobs/${id}/cog/${freq}`,

  nationalCogUrl: (freq: Frequency, region: NationalRegion = "conus", resolutionM = 1000) =>
    `${BASE}/coverage/national?freq=${freq}&region=${region}&resolution_m=${resolutionM}`,

  nationalStatus: (freq: Frequency, region: NationalRegion = "conus", resolutionM = 1000) =>
    req<{
      freq: Frequency;
      region: NationalRegion;
      resolution_m: number;
      inputs: number;
      built: boolean;
      stale: boolean;
      built_at?: number;
      size_bytes?: number;
    }>(`/coverage/national/status?freq=${freq}&region=${region}&resolution_m=${resolutionM}`),

  instantPreview: (bbox: BBox, freq: Frequency = "1090", stationFilter: ConnectedFilter = "connected_only") =>
    req<{
      stations_in_scope: number;
      cached_count: number;
      missing_count: number;
      missing_station_ids: number[];
      cached_station_ids: number[];
    }>(
      `/coverage/instant/preview?bbox=${bbox.lon_min},${bbox.lat_min},${bbox.lon_max},${bbox.lat_max}` +
      `&freq=${freq}&station_filter=${stationFilter}`,
    ),

  instantCogUrl: (bbox: BBox, freq: Frequency = "1090", resolutionM = 200, stationFilter: ConnectedFilter = "connected_only") =>
    `${BASE}/coverage/instant?bbox=${bbox.lon_min},${bbox.lat_min},${bbox.lon_max},${bbox.lat_max}` +
    `&freq=${freq}&resolution_m=${resolutionM}&station_filter=${stationFilter}`,

  instantExport: async (
    bbox: BBox,
    format: "kmz" | "bundle",
    stationIds?: number[],
    stationFilter: ConnectedFilter = "connected_only",
  ): Promise<Blob> => {
    const r = await fetch(`${BASE}/coverage/instant/export`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bbox: `${bbox.lon_min},${bbox.lat_min},${bbox.lon_max},${bbox.lat_max}`,
        format,
        station_ids: stationIds ?? null,
        station_filter: stationFilter,
      }),
    });
    if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
    return r.blob();
  },

  observerCogUrl: (jobId: string, stationId: number, freq: Frequency) =>
    `${BASE}/jobs/${jobId}/observer/${stationId}/cog/${freq}`,

  exportKmzUrl: (id: string) => `${BASE}/jobs/${id}/export`,

  bundleUrl: (id: string) => `${BASE}/jobs/${id}/bundle`,

  importBundle: async (file: File): Promise<{
    id: string;
    bbox_wkt: string;
    imported_from?: string;
    exported_at?: string;
    observers?: ObserverMeta[];
    altitude_tiers_ft?: number[];
  }> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${BASE}/jobs/import-bundle`, { method: "POST", body: form });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },

  eventsUrl: (id: string) => `${BASE}/jobs/${id}/events`,
};
